export interface AthenaPasskeyAuthenticationWire {
  id: string;
  rawId: string;
  type: "public-key";
  authenticatorAttachment?: AuthenticatorAttachment;
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle: string | null;
  };
}

export interface AthenaPasskeyRegistrationWire {
  id: string;
  rawId: string;
  type: "public-key";
  authenticatorAttachment?: AuthenticatorAttachment;
  response: {
    attestationObject: string;
    clientDataJSON: string;
    transports?: string[];
  };
  clientExtensionResults: {
    credProps?: { rk: boolean };
  };
}

type GlobalWebAuthn = typeof globalThis & {
  AuthenticatorAssertionResponse?: typeof AuthenticatorAssertionResponse;
  AuthenticatorAttestationResponse?: typeof AuthenticatorAttestationResponse;
  PublicKeyCredential?: typeof PublicKeyCredential;
};

function toBase64Url(value: ArrayBuffer | ArrayBufferView): string {
  const bytes =
    value instanceof ArrayBuffer
      ? new Uint8Array(value)
      : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);

  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function requirePublicKeyCredential(value: unknown): PublicKeyCredential {
  const CredentialCtor = (globalThis as GlobalWebAuthn).PublicKeyCredential;
  if (!CredentialCtor || !(value instanceof CredentialCtor)) {
    throw new TypeError("Expected a PublicKeyCredential returned by WebAuthn");
  }
  return value;
}

function authenticatorAttachmentOf(
  credential: PublicKeyCredential
): AuthenticatorAttachment | undefined {
  const attachment = credential.authenticatorAttachment;
  if (attachment === "platform" || attachment === "cross-platform") {
    return attachment;
  }
  return undefined;
}

function registrationExtensionResults(
  credential: PublicKeyCredential
): AthenaPasskeyRegistrationWire["clientExtensionResults"] {
  const results = credential.getClientExtensionResults();
  const credProps = results.credProps;
  if (!credProps || typeof credProps !== "object") {
    return {};
  }
  const rk = (credProps as { rk?: unknown }).rk;
  if (typeof rk !== "boolean") {
    return {};
  }
  return { credProps: { rk } };
}

export function serializeRegistrationCredential(
  value: unknown
): AthenaPasskeyRegistrationWire {
  const credential = requirePublicKeyCredential(value);
  const AttestationCtor = (globalThis as GlobalWebAuthn)
    .AuthenticatorAttestationResponse;
  if (
    !AttestationCtor ||
    !(credential.response instanceof AttestationCtor)
  ) {
    throw new TypeError("Expected AuthenticatorAttestationResponse");
  }

  const response = credential.response;
  const transports =
    typeof response.getTransports === "function"
      ? response.getTransports()
      : undefined;

  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: "public-key",
    authenticatorAttachment: authenticatorAttachmentOf(credential),
    response: {
      clientDataJSON: toBase64Url(response.clientDataJSON),
      attestationObject: toBase64Url(response.attestationObject),
      ...(Array.isArray(transports) ? { transports } : {}),
    },
    clientExtensionResults: registrationExtensionResults(credential),
  };
}

export function serializeAuthenticationCredential(
  value: unknown
): AthenaPasskeyAuthenticationWire {
  const credential = requirePublicKeyCredential(value);
  const AssertionCtor = (globalThis as GlobalWebAuthn)
    .AuthenticatorAssertionResponse;
  if (!AssertionCtor || !(credential.response instanceof AssertionCtor)) {
    throw new TypeError("Expected AuthenticatorAssertionResponse");
  }

  const response = credential.response;
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: "public-key",
    authenticatorAttachment: authenticatorAttachmentOf(credential),
    response: {
      authenticatorData: toBase64Url(response.authenticatorData),
      clientDataJSON: toBase64Url(response.clientDataJSON),
      signature: toBase64Url(response.signature),
      userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
    },
  };
}

export function serializeCreatedPasskey(credential: unknown): string {
  return JSON.stringify(serializeRegistrationCredential(credential));
}

export function serializeAssertedPasskey(credential: unknown): string {
  return JSON.stringify(serializeAuthenticationCredential(credential));
}
